/**
 * 전 화면 자동 screen_view 트래킹 — RootLayout 에 1회 마운트.
 *
 * SPA 라 GA 자동 페이지뷰가 없으므로 라우트(pathname) 변경마다 screen_view 를 보낸다.
 * SubActivity(kuji/detail 등)도 useActivityNavigator 가 navigate() 로 URL 을 바꾸므로
 * 같은 경로로 커버된다 (docs/12_NAVIGATION_ACTIVITY_MODEL.md).
 *
 * screen_name 은 동적 세그먼트를 라우트 패턴으로 정규화해 보낸다:
 *   /kuji/123 → /kuji/:kujiId   (화면이 ID 별로 수천 개로 흩어지는 것 방지)
 * 방식: useMatches 의 최종 match params 값을 pathname 세그먼트에서 :키 로 역치환.
 * (react-router 는 라우트 패턴 문자열을 직접 노출하지 않는다 — params 역치환이
 *  라우트 트리 수정 없이 결정적으로 동작하는 유일한 자동 방법.)
 */
import { useEffect, useRef } from "react";
import { useLocation, useMatches } from "react-router-dom";
import { logScreenView } from "../lib/analytics";

/**
 * pathname 의 동적 세그먼트를 params 로 역치환해 라우트 패턴을 복원한다.
 * - 세그먼트 전체가 param 값과 일치할 때만 치환 (부분 일치 없음 — 오치환 방지)
 * - URL 인코딩된 세그먼트는 디코드 후 비교
 * - splat(*) param 은 나머지 경로 전체를 "*" 로 접는다
 */
export function normalizeScreenName(
  pathname: string,
  params: Record<string, string | undefined>,
): string {
  if (!pathname) return "/";

  const splat = params["*"];
  let path = pathname;
  if (splat && splat.length > 0 && path.endsWith(splat)) {
    path = `${path.slice(0, path.length - splat.length)}*`;
  }

  const valueToKey = Object.entries(params).filter(
    (entry): entry is [string, string] =>
      entry[0] !== "*" && typeof entry[1] === "string" && entry[1].length > 0,
  );
  if (valueToKey.length === 0) return path;

  const segments = path.split("/").map((segment) => {
    if (!segment || segment === "*") return segment;
    let decoded = segment;
    try {
      decoded = decodeURIComponent(segment);
    } catch {
      // malformed escape — 원본 세그먼트로 비교
    }
    const hit = valueToKey.find(([, value]) => value === decoded || value === segment);
    return hit ? `:${hit[0]}` : segment;
  });
  return segments.join("/") || "/";
}

export function useScreenTracking(): void {
  const location = useLocation();
  const matches = useMatches();
  // 같은 pathname 재발화 방지 — StrictMode 더블 이펙트/재렌더에서 1회만 전송.
  const lastPathname = useRef<string | null>(null);

  useEffect(() => {
    if (lastPathname.current === location.pathname) return;
    lastPathname.current = location.pathname;

    const leaf = matches[matches.length - 1];
    const params = (leaf?.params ?? {}) as Record<string, string | undefined>;
    logScreenView(normalizeScreenName(location.pathname, params), location.pathname);
    // matches 는 렌더마다 참조가 바뀌지만 pathname 이 같으면 위 가드가 재전송을 막는다.
  }, [location.pathname, matches]);
}
